import {SubscriptionPlan, SubscriptionPlanShowcase} from "@/subscription-plan/interfaces";
import {AxiosInstance} from "axios";
import {Trial} from "@/core/interfaces/trial";
import {ShowcaseItem} from "@/core/interfaces/showcase";
import {TaskResult} from "@/core/interfaces/task-result";

/**
 * Subscription plan resource.
 */
export default function (client: AxiosInstance) {
    return {
        /**
         * Get the subscription plans.
         */
        async index(): Promise<SubscriptionPlan[]> {
            return client
                .get('/subscription-plans')
                .then(response => response.data.data);
        },

        /**
         * Read a subscription plan.
         *
         * @param id
         */
        async read(id: number): Promise<SubscriptionPlan> {
            return client
                .get(`/subscription-plans/${id}`)
                .then(response => response.data.data);
        },

        /**
         * Launch a trial for the subscription plan.
         *
         * @param planId
         */
        async launchTrial(planId: number): Promise<Trial> {
            return client
                .post(`/subscription-plans/${planId}/trials`)
                .then(response => response.data.data);
        },

        /**
         * Get the showcases that belongs to subscription plan extensions.
         */
        async showcases(planId: number, abortController?: AbortController): Promise<{
            extension: { id: number };
            showcases: ShowcaseItem[]
        }[]> {
            return client.get(
                `/subscription-plans/${planId}/showcases`,
                {signal: abortController?.signal}
            )
                .then(response => response.data.data);
        },

        /**
         * Run the compute showcases task for the subscription plan.
         * @param planId
         */
        async computeShowcases(planId: number): Promise<TaskResult<SubscriptionPlanShowcase[]>> {
            return client.post(`/subscription-plans/${planId}/showcases`)
                .then(response => response.data.data);
        },

        /**
         * Get the status of the compute showcases task for the subscription plan.
         */
        async showcasesStatus(planId: number, taskId: number): Promise<TaskResult<SubscriptionPlanShowcase[]>> {
            return client.get(`/subscription-plans/${planId}/showcases/${taskId}`)
                .then(response => response.data.data);
        },

        /**
         * Enable the extensions that belongs to the subscription plan.
         * @param planId
         */
        async enableExtensions(planId: number): Promise<void> {
            return client.patch(`subscription-plans/${planId}/extensions/enable`);
        },

        /**
         * Disable the extensions that belongs to the subscription plan.
         * @param planId
         */
        async disableExtensions(planId: number): Promise<void> {
            return client.patch(`subscription-plans/${planId}/extensions/disable`);
        }
    };
}
